/**
 * Foto — MOBILE.
 *
 * `capture="environment"` abre la cámara trasera directo: se fotografía la balanza parado
 * encima. Los campos llegan precargados y editables, con los dudosos resaltados en ámbar, y el
 * botón de guardar no se habilita hasta que el nivel de resistencia esté puesto (§4.5.5).
 */

import { useRef, type ReactNode } from 'react';
import type { Condiciones } from '@mg/core';

import { useIngesta } from '../features/useIngesta.ts';
import { Aviso, Campo, Icono, Insignia, Selector, Vacio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

export function IngestaMobile(): ReactNode {
  const g = useIngesta();
  const { ir } = useRuta();
  const input = useRef<HTMLInputElement>(null);

  if (g.datos.cargando) return <Vacio>Cargando…</Vacio>;

  if (g.paso === 'listo') {
    return (
      <>
        <Aviso tono="info" titulo="Guardado">
          {g.tipo === 'balanza'
            ? 'La medición quedó registrada. El p-ratio se recalcula solo.'
            : 'El cardio quedó asociado a la sesión de hoy.'}
        </Aviso>
        <button type="button" className="boton boton--principal" onClick={g.reiniciar}>
          Cargar otra foto
        </button>
        <button
          type="button"
          className="boton boton--fantasma"
          onClick={() => ir(g.tipo === 'balanza' ? 'cuerpo' : 'registro')}
        >
          {g.tipo === 'balanza' ? 'Ver composición' : 'Volver a la sesión'}
        </button>
      </>
    );
  }

  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <button
          type="button"
          className={g.tipo === 'balanza' ? 'boton boton--principal crece' : 'boton crece'}
          onClick={() => {
            g.reiniciar();
            g.setTipo('balanza');
          }}
        >
          Balanza
        </button>
        <button
          type="button"
          className={g.tipo === 'consola_cardio' ? 'boton boton--principal crece' : 'boton crece'}
          onClick={() => {
            g.reiniciar();
            g.setTipo('consola_cardio');
          }}
        >
          Consola de cardio
        </button>
      </div>

      {/* Que falte la sesión no bloquea nada, pero descubrirlo al confirmar sí desconcierta. */}
      {g.abriraSesion && (
        <Aviso tono="info">
          El cardio se guarda en la sesión de hoy. No hay una abierta: se abre sola al confirmar.
        </Aviso>
      )}

      {g.paso === 'elegir' && (
        <>
          <Aviso tono={g.tipo === 'consola_cardio' ? 'atencion' : 'info'}>
            {g.tipo === 'balanza'
              ? 'Enfocá la pantalla de la balanza de frente, sin reflejos. Se leen hasta doce métricas.'
              : 'Los display de siete segmentos se leen mal de noche y en ángulo: el 8 y el 0, el 3 y el 9. Vas a tener que revisar los números.'}
          </Aviso>

          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void g.leer(f);
            }}
          />

          <button
            type="button"
            className="boton boton--principal boton--registrar"
            onClick={() => input.current?.click()}
          >
            <Icono nombre="camara" tamano={24} />
            Sacar la foto
          </button>

          <p className="nota--fina" style={{ textAlign: 'center' }}>
            Vas a poder revisar y corregir los números antes de guardar.
          </p>

          {g.error && (
            <Aviso tono="importante">
              {g.error}
              {/* Sin red no hay nada que reintentar solo: la salida es cargarlo a mano. */}
              {g.sinRed && g.tipo === 'balanza' && (
                <div style={{ marginTop: 'var(--e2)' }}>
                  <button type="button" className="boton" onClick={() => ir('cuerpo', 'medir')}>
                    Cargarla a mano
                  </button>
                </div>
              )}
              {g.sinRed && g.tipo === 'consola_cardio' && (
                <p style={{ marginTop: 'var(--e2)' }}>
                  La consola sólo se lee desde la foto: probá de nuevo cuando vuelva la señal.
                </p>
              )}
            </Aviso>
          )}
        </>
      )}

      {g.paso === 'leyendo' && (
        <>
          {g.vistaPrevia && (
            <img
              src={g.vistaPrevia}
              alt="La foto que se está leyendo"
              style={{ width: '100%', borderRadius: 'var(--r-grande)', border: '1px solid var(--hierro-borde)' }}
            />
          )}
          <p className="nota" style={{ textAlign: 'center' }}>
            Leyendo la pantalla…
          </p>
        </>
      )}

      {g.paso === 'revisar' && (
        <>
          <Aviso tono="atencion" titulo="Revisá antes de guardar">
            Nada se guarda hasta que confirmes. Los campos en ámbar son los que el modelo leyó con
            poca confianza.
          </Aviso>

          {g.tipo === 'balanza' && g.balanza && <FormularioBalanza g={g} />}
          {g.tipo === 'consola_cardio' && g.cardio && <FormularioCardio g={g} />}

          {g.errores.length > 0 && (
            <Aviso tono="importante" titulo="Eso no se puede guardar">
              {g.errores.map((h) => h.mensaje).join(' ')}
            </Aviso>
          )}

          {g.errores.length === 0 && g.aConfirmar.length > 0 && (
            <Aviso tono="atencion">
              {g.aConfirmar.map((h) => h.mensaje).join(' ')}
              <label className="fila" style={{ marginTop: 'var(--e2)', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={g.confirmado}
                  onChange={(e) => g.setConfirmado(e.target.checked)}
                  style={{ width: 22, height: 22 }}
                />
                <span>Está bien, es correcto</span>
              </label>
            </Aviso>
          )}

          {g.faltaNivel && (
            <Aviso tono="atencion" titulo="Falta el nivel de resistencia">
              No sale en la foto y sin él esta sesión no se puede comparar con las otras: más
              distancia en el mismo tiempo puede ser sólo un nivel más bajo.
            </Aviso>
          )}

          <button
            type="button"
            className="boton boton--principal boton--registrar"
            disabled={!g.puedeGuardar || g.guardando}
            onClick={() => void g.confirmar()}
          >
            {g.guardando ? 'Guardando…' : 'Confirmar y guardar'}
          </button>

          <button type="button" className="boton boton--fantasma boton--chico" onClick={g.reiniciar}>
            Descartar y sacar otra
          </button>

          {g.error && <Aviso tono="importante">{g.error}</Aviso>}
        </>
      )}
    </>
  );
}

function FormularioBalanza({ g }: { g: ReturnType<typeof useIngesta> }): ReactNode {
  const b = g.balanza!;
  const set = (k: keyof typeof b, v: string) => g.setBalanza({ ...b, [k]: v });

  const campos: [keyof typeof b, string, string][] = [
    ['peso', 'Peso (kg)', 'peso'],
    ['grasa_pct', 'Grasa %', 'grasa_pct'],
    ['masa_magra', 'Masa magra', 'masa_magra'],
    ['masa_muscular', 'Muscular', 'masa_muscular'],
    ['masa_osea', 'Ósea', 'masa_osea'],
    ['agua_pct', 'Agua %', 'agua_pct'],
    ['grasa_visceral', 'Visceral', 'grasa_visceral'],
    ['imc', 'IMC', 'imc'],
    ['tmb', 'TMB', 'tmb'],
  ];

  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="Fecha" texto type="date" value={g.fecha} onChange={(e) => g.setFecha(e.target.value)} />
        </div>
        <div style={{ width: 110 }}>
          <Campo etiqueta="Hora" texto type="time" value={g.hora} onChange={(e) => g.setHora(e.target.value)} />
        </div>
      </div>

      {campos.map(([clave, etiqueta, campoOCR]) => (
        <Campo
          key={clave}
          etiqueta={etiqueta}
          inputMode="decimal"
          revisar={g.revisar.has(campoOCR)}
          value={b[clave]}
          onChange={(e) => set(clave, e.target.value)}
        />
      ))}

      <Selector
        etiqueta="Condiciones"
        value={g.condiciones}
        onChange={(e) => g.setCondiciones(e.target.value as Condiciones)}
      >
        <option value="ayuno">En ayunas</option>
        <option value="post_entreno">Después de entrenar</option>
        <option value="desconocido">No me acuerdo</option>
      </Selector>
    </>
  );
}

function FormularioCardio({ g }: { g: ReturnType<typeof useIngesta> }): ReactNode {
  const c = g.cardio!;
  const set = (k: keyof typeof c, v: string) => g.setCardio({ ...c, [k]: v });

  return (
    <>
      <Campo
        etiqueta="Tiempo (mm:ss)"
        revisar={g.revisar.has('duracion_seg')}
        value={c.duracion}
        onChange={(e) => set('duracion', e.target.value)}
        placeholder="30:34"
      />
      <Campo
        etiqueta="Distancia (km)"
        inputMode="decimal"
        revisar={g.revisar.has('distancia_km')}
        value={c.distancia_km}
        onChange={(e) => set('distancia_km', e.target.value)}
      />
      <Campo
        etiqueta="Calorías"
        inputMode="numeric"
        revisar={g.revisar.has('calorias')}
        value={c.calorias}
        onChange={(e) => set('calorias', e.target.value)}
      />
      <Campo
        etiqueta="Nivel de resistencia"
        inputMode="numeric"
        revisar={g.faltaNivel}
        value={c.nivel_resistencia}
        onChange={(e) => set('nivel_resistencia', e.target.value)}
        ayuda="No sale en la foto. Sin esto, la sesión no es comparable."
      />
      <Campo etiqueta="Máquina" texto value={c.maquina_tipo} onChange={(e) => set('maquina_tipo', e.target.value)} />

      {g.noLegibles.length > 0 && (
        <p className="nota--fina">
          El modelo dijo que no pudo leer: {g.noLegibles.join(', ')}.{' '}
          <Insignia tono="ambar">no inventó nada</Insignia>
        </p>
      )}
    </>
  );
}
